import type { ProjectRole } from "../../shared/api/types";

/**
 * Guided tours are DATA. To add one for a release: append a TourDef below with a new `id` (or bump `version` of an
 * existing tour to show it again), point each step at a `data-guide="…"` attribute on the element it explains, and write
 * the text in Thai. Everyone who has not seen that id at that version is offered it once (GET/PUT /me/guides).
 *
 * Content rules: Thai, plain and friendly, short sentences; no internal jargon, file or technical names; name buttons by
 * the English label people see on screen. App chrome stays English (17 Sep ruling); tour content is Thai (1 Oct ruling).
 */

/** Thai text, with an optional English fallback used only when the Thai is missing. */
export interface Localized {
  th: string;
  en?: string;
}

/** Who may see a tour: any project role held in any project, or SystemAdmin. */
export type AudienceRole = ProjectRole | "SystemAdmin";

export interface TourStep {
  /** The `data-guide` value of the element to spotlight. Absent: a centred step with no highlight. A step whose element is
   * not on the page is skipped. */
  target?: string;
  title: Localized;
  body: Localized;
  /**
   * Batch 18 §11.4: below 760 px some controls live inside the account menu (D-B17-03), so `target` is not on the page. A
   * step may name what to point at on a phone instead, and say how to get there. The tour never opens the menu itself.
   */
  phoneTarget?: string;
  phoneBody?: Localized;
}

export interface TourDef {
  /** Stable id stored per person on the server (lower-case letters, digits, hyphens). Never reuse one for another tour. */
  id: string;
  /** Bump to offer the tour again to people who saw an earlier version. */
  version: number;
  /** ISO date of the release that shipped the feature; what's-new tours are offered in this order. */
  releasedOn: string;
  /** `welcome` starts by itself on a first sign-in; `whatsNew` is offered with a small prompt. */
  kind: "welcome" | "whatsNew";
  title: Localized;
  /** One line under the title in the prompt and the Guides menu. */
  summary: Localized;
  /** Any of these roles; absent means everyone. */
  roles?: readonly AudienceRole[];
  /** Hidden while this feature flag (name without `Features__`) is off. */
  flag?: string;
  /** false: never shown to partners. Default true. */
  partners?: boolean;
  /** Route pattern (react-router) the tour belongs to. It is offered only on that page. Absent: any page. */
  page?: string;
  /** Where the tour works, shown in the Guides menu when it cannot start from the current page. */
  pageName?: Localized;
  /** For Replay from another page: where to go first, in the current project. Absent: the person opens the page. */
  openPath?: (projectKey: string) => string;
  steps: readonly TourStep[];
}

export const WELCOME_TOUR_ID = "welcome";

const itemPage = { th: "หน้ารายละเอียดงาน (เปิดงานใดก็ได้)" };

export const TOURS: readonly TourDef[] = [
  {
    id: WELCOME_TOUR_ID,
    version: 1,
    releasedOn: "2026-10-01",
    kind: "welcome",
    title: { th: "ยินดีต้อนรับสู่ Giraffe", en: "Welcome to Giraffe" },
    summary: { th: "พาดูส่วนหลักของหน้าจอทีละจุด ใช้เวลาไม่ถึงหนึ่งนาที" },
    steps: [
      {
        title: { th: "ยินดีต้อนรับ" },
        body: { th: "เราจะพาดูส่วนหลักของหน้าจอทีละจุด กด ถัดไป เพื่อไปต่อ หรือกด ข้าม ได้ทุกเมื่อ เปิดดูอีกครั้งได้จากเมนูชื่อของคุณมุมขวาบน" },
      },
      {
        target: "project-picker",
        title: { th: "เลือกโครงการ" },
        body: { th: "เลือกโครงการที่จะทำงานจากตรงนี้ เมนูด้านซ้ายจะเปลี่ยนไปตามโครงการที่เลือก" },
      },
      {
        target: "sidebar",
        title: { th: "เมนูหลัก" },
        body: { th: "เมนูแบ่งเป็นหมวด Home, Plan, Operate, Register และ Settings แต่ละคนจะเห็นเมนูตามสิทธิ์ของตัวเอง" },
      },
      {
        target: "nav-items",
        title: { th: "รายการงาน" },
        body: {
          th: "Items คือรายการงานทั้งหมดของโครงการ กดที่งานใดก็ได้เพื่อเปิดหน้ารายละเอียด ในหน้านั้นจะเห็นสถานะ ปุ่มเปลี่ยนสถานะ ความคิดเห็น และประวัติการเปลี่ยนแปลงของงาน",
        },
      },
      {
        target: "nav-board",
        title: { th: "บอร์ด" },
        body: { th: "Board แสดงงานเป็นการ์ดเรียงตามสถานะ ลากการ์ดไปอีกช่องเพื่อเปลี่ยนสถานะได้" },
      },
      {
        target: "nav-backlog",
        title: { th: "แบ็กล็อก" },
        body: { th: "Backlog คือรายการงานที่รอวางแผน จัดลำดับความสำคัญ และย้ายงานเข้าสปรินต์ได้จากที่นี่" },
      },
      {
        target: "search",
        title: { th: "ค้นหา" },
        body: { th: "พิมพ์คำที่ต้องการ รหัสงาน หรือเลขอ้างอิงของงาน แล้วกด Enter กดปุ่ม / บนแป้นพิมพ์เพื่อมาที่ช่องนี้ได้ทันที" },
      },
      {
        target: "create",
        title: { th: "สร้างงานใหม่" },
        body: { th: "กด Create เพื่อสร้างงานใหม่ในโครงการที่เลือกอยู่ หรือกดปุ่ม c บนแป้นพิมพ์" },
      },
      // Batch 17 attention-bell merge §5: with the bell on, the header "Needs attention" pill is gone and its number sits beside the
      // bell, so the step points at the bell; with the bell off (or for an AI agent's account) the pill is still there. Exactly
      // one of the two targets is on the page, and a step whose element is missing is skipped, so the tour never points at
      // nothing (AC-M20). No version bump: the engine re-offers a tour only when its version is bumped, and the change does not
      // warrant showing the whole welcome tour again to everyone who has seen it.
      {
        target: "notifications",
        title: { th: "เรื่องที่รอคุณและการแจ้งเตือน" },
        body: {
          th: "ตัวเลขข้างกระดิ่งบอกจำนวนเรื่องที่รอคุณอยู่ จุดบนกระดิ่งแปลว่ามีการแจ้งเตือนที่ยังไม่อ่าน กดเพื่อดูทั้งสองอย่าง หรือกดปุ่ม n บนแป้นพิมพ์",
        },
      },
      {
        target: "attention",
        title: { th: "เรื่องที่รอคุณ" },
        body: { th: "ตัวเลขตรงนี้บอกจำนวนเรื่องที่รอคุณอยู่ กดเพื่อดูรายการทั้งหมด" },
      },
      {
        target: "user-menu",
        title: { th: "เมนูของคุณ" },
        body: { th: "เปิดเมนูนี้เพื่อดูว่าวันนี้เปิดใช้อะไรบ้าง ตั้งค่าอีเมลแจ้งเตือน ออกจากระบบ และเปิดคู่มือทั้งหมดดูอีกครั้งได้ในหัวข้อ Guides" },
      },
    ],
  },
  {
    id: "backlog-search-load-more",
    version: 1,
    releasedOn: "2026-09-30",
    kind: "whatsNew",
    title: { th: "Backlog: ค้นหาและดูรายการเพิ่ม" },
    summary: { th: "ค้นหางานในแบ็กล็อก และดูได้ครบทุกรายการ" },
    flag: "Sprints",
    partners: false,
    page: "/p/:projectKey/backlog",
    pageName: { th: "หน้า Backlog" },
    openPath: (projectKey) => `/p/${encodeURIComponent(projectKey)}/backlog`,
    steps: [
      {
        target: "backlog-search",
        title: { th: "ค้นหาในแบ็กล็อก" },
        body: { th: "พิมพ์รหัสงานหรือชื่องานเพื่อค้นหา ระหว่างค้นหายังย้ายงานเข้าสปรินต์ได้เหมือนเดิม ล้างคำค้นเมื่อต้องการจัดลำดับงาน" },
      },
      {
        target: "backlog-load-more",
        title: { th: "ดูรายการเพิ่ม" },
        body: { th: "แบ็กล็อกแสดงงานทีละชุด ถ้ามีงานมากกว่าที่เห็น กด Load more เพื่อดูชุดถัดไป" },
      },
    ],
  },
  {
    id: "item-move-to-sprint",
    version: 1,
    releasedOn: "2026-09-30",
    kind: "whatsNew",
    title: { th: "ย้ายงานเข้าสปรินต์จากหน้างาน" },
    summary: { th: "ไม่ต้องกลับไปที่แบ็กล็อกเพื่อย้ายงานเข้าสปรินต์" },
    flag: "Sprints",
    partners: false,
    page: "/items/:ref",
    pageName: itemPage,
    steps: [
      {
        target: "item-move-to-sprint",
        title: { th: "ย้ายเข้าสปรินต์ได้จากหน้านี้" },
        body: { th: "กด Move to sprint แล้วเลือกสปรินต์ที่ต้องการ งานจะย้ายเข้าสปรินต์นั้นทันที ไม่ต้องกลับไปที่แบ็กล็อก" },
      },
    ],
  },
  {
    id: "escalation-joins-active-sprint",
    version: 1,
    releasedOn: "2026-09-30",
    kind: "whatsNew",
    title: { th: "งานที่ยกระดับจากงานบริการ เข้าสปรินต์ให้เอง" },
    summary: { th: "งานใหม่จากการยกระดับขึ้นบอร์ดทันที" },
    flag: "Sprints",
    partners: false,
    roles: ["ServiceDeskAgent", "ProjectManager", "ProjectDirector", "ProjectAdmin", "SystemAdmin"],
    page: "/p/:projectKey/board",
    pageName: { th: "หน้า Board" },
    openPath: (projectKey) => `/p/${encodeURIComponent(projectKey)}/board`,
    steps: [
      {
        target: "board",
        title: { th: "งานที่ยกระดับขึ้นบอร์ดทันที" },
        body: {
          th: "เมื่อยกระดับคำขอบริการหรือเหตุขัดข้องเป็นงานใหม่ งานนั้นจะเข้าสปรินต์ที่กำลังดำเนินอยู่ของโครงการให้เอง และขึ้นบนบอร์ดนี้ ถ้าโครงการยังไม่มีสปรินต์ที่กำลังดำเนินอยู่ งานจะรออยู่ในแบ็กล็อกเหมือนเดิม งานที่ยกระดับไว้ก่อนหน้านี้จะไม่ถูกย้าย",
        },
      },
    ],
  },
  {
    id: "item-history-readable",
    version: 1,
    releasedOn: "2026-09-30",
    kind: "whatsNew",
    title: { th: "ประวัติของงาน อ่านง่ายและครบขึ้น" },
    summary: { th: "ดูได้ว่าใครเปลี่ยนอะไร กรอกอะไร และงานเคยอยู่สปรินต์ไหน" },
    page: "/items/:ref",
    pageName: itemPage,
    steps: [
      {
        target: "item-tab-history",
        title: { th: "อ่านง่ายขึ้น" },
        body: { th: "แท็บ History บอกว่าใครเปลี่ยนอะไร เมื่อไร จากค่าไหนเป็นค่าไหน โดยใช้ชื่อช่องและชื่อสถานะแบบที่เห็นบนหน้าจอ" },
      },
      {
        target: "item-tab-history",
        title: { th: "บันทึกสิ่งที่กรอกตอนเปลี่ยนสถานะ" },
        body: { th: "เวลาเปลี่ยนสถานะแล้วกรอกข้อมูลในหน้าต่างที่ขึ้นมา ค่าที่กรอกจะถูกบันทึกไว้ในประวัติด้วย" },
      },
      {
        target: "item-tab-history",
        title: { th: "ทุกการย้ายสปรินต์มีบันทึก" },
        body: { th: "ทุกครั้งที่งานถูกย้ายเข้าหรือออกจากสปรินต์ จะมีบรรทัดบันทึกไว้ในประวัติ ย้อนดูได้ว่างานเคยอยู่สปรินต์ไหนบ้าง" },
      },
    ],
  },
  {
    id: "status-names-english",
    version: 1,
    releasedOn: "2026-09-30",
    kind: "whatsNew",
    title: { th: "ชื่อสถานะเป็นภาษาอังกฤษ" },
    summary: { th: "ชื่อสถานะของงานแสดงแบบเดียวกันสำหรับทุกคน" },
    page: "/items/:ref",
    pageName: itemPage,
    steps: [
      {
        target: "item-status",
        title: { th: "ชื่อสถานะของงาน" },
        body: { th: "ชื่อสถานะของงานแสดงเป็นภาษาอังกฤษเสมอ ไม่ว่าจะตั้งภาษาไว้แบบใด กดที่สถานะเพื่อดูขั้นตอนทั้งหมดของงานประเภทนี้" },
      },
    ],
  },
  {
    id: "workflow-diagram",
    version: 1,
    releasedOn: "2026-09-30",
    kind: "whatsNew",
    title: { th: "ดูขั้นตอนการทำงานเป็นแผนภาพ" },
    summary: { th: "เห็นทุกสถานะและทางเปลี่ยนสถานะในภาพเดียว" },
    roles: ["SystemAdmin", "ProjectAdmin"],
    partners: false,
    page: "/p/:projectKey/settings/workflows/:workType",
    pageName: { th: "หน้า Workflows แล้วเลือกงานประเภทใดก็ได้" },
    steps: [
      {
        target: "workflow-view-toggle",
        title: { th: "สลับมุมมอง" },
        body: { th: "หน้าขั้นตอนการทำงานเปิดเป็นแผนภาพก่อน สลับไปดูแบบข้อความได้ที่ปุ่ม Diagram และ Text" },
      },
      {
        target: "workflow-diagram",
        title: { th: "แผนภาพขั้นตอน" },
        body: { th: "แผนภาพแสดงทุกสถานะและทางเปลี่ยนสถานะของงานประเภทนี้ กดที่สถานะหรือเส้นเพื่อดูรายละเอียด ลากพื้นหลังเพื่อเลื่อนดู" },
      },
    ],
  },
  {
    id: "test-mode-view-as",
    version: 1,
    releasedOn: "2026-09-30",
    kind: "whatsNew",
    title: { th: "ทดลองดูในมุมของคนอื่น (View as)" },
    summary: { th: "ตรวจว่าแต่ละบทบาทหรือแต่ละคนเห็นและทำอะไรได้บ้าง" },
    roles: ["SystemAdmin"],
    flag: "RoleTestMode",
    partners: false,
    steps: [
      {
        target: "test-mode",
        title: { th: "ดูในมุมของคนอื่น" },
        body: { th: "เลือกบทบาทในโครงการ หรือเลือกผู้ใช้ เพื่อดูว่าเขาเห็นและทำอะไรได้บ้าง ระหว่างนี้แถบด้านบนจะบอกว่ากำลังอยู่ในโหมดทดสอบ" },
        phoneTarget: "user-menu",
        phoneBody: {
          th: "แตะรูปของคุณที่มุมขวาบน แล้วเลือก View as จากนั้นเลือกบทบาทในโครงการ หรือเลือกผู้ใช้ เพื่อดูว่าเขาเห็นและทำอะไรได้บ้าง ระหว่างนี้แถบด้านบนจะบอกว่ากำลังอยู่ในโหมดทดสอบ",
        },
      },
      {
        target: "test-mode",
        phoneTarget: "user-menu",
        title: { th: "ทุกอย่างยังเป็นชื่อคุณ" },
        body: { th: "การเปลี่ยนแปลงที่ทำระหว่างนี้บันทึกในชื่อของคุณ พร้อมหมายเหตุว่าทำในโหมดทดสอบ เลือก Off เพื่อกลับไปใช้สิทธิ์จริงของคุณ" },
      },
    ],
  },
  {
    id: "workflow-diagram-edit",
    version: 1,
    releasedOn: "2026-10-01",
    kind: "whatsNew",
    title: { th: "แก้ขั้นตอนการทำงานบนแผนภาพ" },
    summary: { th: "เพิ่มสถานะและทางเปลี่ยนสถานะได้บนแผนภาพ แล้วตรวจก่อนเผยแพร่" },
    roles: ["SystemAdmin", "ProjectAdmin"],
    partners: false,
    page: "/p/:projectKey/settings/workflows/:workType/draft",
    pageName: { th: "หน้า Workflows เลือกงานประเภทใดก็ได้ แล้วกด Edit" },
    steps: [
      {
        target: "workflow-diagram",
        title: { th: "แก้บนแผนภาพได้เลย" },
        body: {
          th: "ในฉบับร่าง ลากจากสถานะหนึ่งไปอีกสถานะเพื่อเพิ่มทางเปลี่ยนสถานะ หรือกด + Move ส่วน + Status ใช้เพิ่มสถานะ กดที่เส้นเพื่อแก้รายละเอียด หรือกด Delete เพื่อลบ",
        },
      },
      {
        target: "workflow-draft-actions",
        title: { th: "บันทึก แล้วตรวจก่อนเผยแพร่" },
        body: {
          th: "กด Save เพื่อเก็บฉบับร่าง เมื่อพร้อมแล้วกด Review and publish เพื่อดูสรุปการเปลี่ยนแปลงก่อนใช้งานจริง งานที่มีอยู่จะไม่ถูกเปลี่ยนสถานะ",
        },
      },
    ],
  },
  {
    id: "cr-customer-approved",
    version: 1,
    releasedOn: "2026-10-01",
    kind: "whatsNew",
    title: { th: "อนุมัติ CR จากลูกค้าได้ง่ายขึ้น" },
    summary: { th: "กรอกแค่เลขอ้างอิงและวันที่ แล้วระบุหมวดของไฟล์แนบ" },
    partners: false,
    page: "/items/:ref",
    pageName: { th: "หน้ารายละเอียดของ Change request (เปิด CR ใดก็ได้)" },
    steps: [
      {
        target: "item-cr-status",
        title: { th: "Customer approved กรอกแค่สองช่อง" },
        body: {
          th: "เมื่อลูกค้าอนุมัติ CR แล้ว กด Customer approved แล้วกรอกเลขอ้างอิงการอนุมัติกับวันที่อนุมัติเท่านั้น ไม่ต้องแนบแบบฟอร์มก่อนจึงจะกดได้อีกต่อไป",
        },
      },
      {
        target: "item-cr-attachments",
        title: { th: "ระบุหมวดของไฟล์แนบ" },
        body: {
          th: "ในแท็บ Attachments เลือกหมวดของไฟล์ได้ตอนแนบ หรือเปลี่ยนภายหลังก็ได้ เช่น ใบอนุมัติจากลูกค้า หลักฐานการทดสอบ หรือเอกสารออกแบบ ทำให้หาไฟล์ที่ต้องการได้เร็วขึ้น",
        },
      },
    ],
  },
  {
    id: "item-add-to-deployment",
    version: 1,
    releasedOn: "2026-10-01",
    kind: "whatsNew",
    title: { th: "ใส่งานเข้ารอบติดตั้งจากหน้างาน" },
    summary: { th: "เลือกรอบติดตั้งที่เปิดอยู่ หรือสร้างรอบใหม่ได้ในที่เดียว" },
    partners: false,
    page: "/items/:ref",
    pageName: { th: "หน้ารายละเอียดของงานที่พร้อมติดตั้ง" },
    steps: [
      {
        target: "item-add-to-deployment",
        title: { th: "ปุ่ม Add to deployment" },
        body: {
          th: "เมื่องานพร้อมติดตั้งแล้ว กด Add to deployment จะเห็นรายการรอบติดตั้งที่เปิดอยู่ของโครงการ เลือกรอบที่ต้องการ หรือเลือก New deployment เพื่อสร้างรอบใหม่พร้อมใส่งานนี้ไปด้วยเลย",
        },
      },
    ],
  },
  {
    id: "deployments-new",
    version: 1,
    releasedOn: "2026-10-01",
    kind: "whatsNew",
    title: { th: "สร้างรอบติดตั้งใหม่" },
    summary: { th: "ปุ่ม New deployment และแบบฟอร์มที่เลือกงานได้ในหน้าเดียว" },
    partners: false,
    page: "/p/:projectKey/deployments",
    pageName: { th: "หน้า Deployments" },
    openPath: (projectKey) => `/p/${encodeURIComponent(projectKey)}/deployments`,
    steps: [
      {
        target: "deployments-new",
        title: { th: "ปุ่ม New deployment" },
        body: {
          th: "กด New deployment เพื่อเปิดแบบฟอร์มสร้างรอบติดตั้ง กรอกรายละเอียดของรอบ แล้วเลือกสภาพแวดล้อม แล้วเลือกงานที่พร้อมติดตั้งจากรายการในแบบฟอร์มเดียวกัน กด More cards ถ้ามีงานมากกว่าที่เห็น",
        },
      },
    ],
  },
  {
    id: "status-filter-open-by-default",
    version: 1,
    releasedOn: "2026-10-01",
    kind: "whatsNew",
    title: { th: "รายการงานเปิดมาเห็นงานที่ยังไม่เสร็จก่อน" },
    summary: { th: "ตัวกรองสถานะเลือก To do และ In progress ไว้ให้" },
    page: "/p/:projectKey",
    pageName: { th: "หน้า Items" },
    openPath: (projectKey) => `/p/${encodeURIComponent(projectKey)}`,
    steps: [
      {
        target: "status-filter",
        title: { th: "เห็นงานที่ยังไม่เสร็จก่อน" },
        body: {
          th: "เมื่อเปิดรายการงาน ตัวกรอง Status จะเลือก To do และ In progress ไว้ให้ จึงเห็นเฉพาะงานที่ยังต้องทำ อยากดูงานที่เสร็จแล้วด้วย กดเลือก Done เพิ่มได้เลย",
        },
      },
    ],
  },
  {
    id: "click-outside-closes",
    version: 1,
    releasedOn: "2026-10-01",
    kind: "whatsNew",
    title: { th: "คลิกนอกหน้าต่างเพื่อปิด" },
    summary: { th: "ปิดหน้าต่างและเมนูได้เร็วขึ้น" },
    steps: [
      {
        title: { th: "คลิกนอกหน้าต่างเพื่อปิด" },
        body: {
          th: "หน้าต่างที่เปิดขึ้นมาและเมนูต่าง ๆ ปิดได้ด้วยการคลิกที่ว่างข้างนอก หรือกดปุ่ม Esc บนแป้นพิมพ์ ถ้ายังมีข้อมูลที่กรอกค้างไว้ ระบบจะถามก่อนปิดเสมอ",
        },
      },
    ],
  },
  {
    id: "item-add-child",
    version: 1,
    releasedOn: "2026-10-01",
    kind: "whatsNew",
    title: { th: "เพิ่มงานย่อยจากหน้างาน" },
    summary: { th: "สร้างงานย่อยใต้ Issue, Action หรือ Change request ได้ทันที" },
    page: "/items/:ref",
    pageName: { th: "หน้ารายละเอียดของ Issue, Action หรือ Change request" },
    steps: [
      {
        target: "item-add-child",
        title: { th: "ปุ่ม Add child" },
        body: {
          th: "กด Add child เพื่อสร้างงานย่อยใต้งานนี้ กรอกเสร็จแล้วงานย่อยจะขึ้นในรายการ Child items ทันที ไม่ต้องไปสร้างแยกแล้วค่อยเชื่อมกันเอง",
        },
      },
    ],
  },
  {
    id: "pin-feedback",
    version: 1,
    releasedOn: "2026-10-01",
    kind: "whatsNew",
    title: { th: "แจ้งปัญหาหรือขอปรับปรุง ด้วยปุ่ม Feedback" },
    summary: { th: "ปักหมุดตรงจุดบนหน้าจอ แล้วเขียนสิ่งที่พบ" },
    flag: "PinFeedback",
    partners: false,
    steps: [
      {
        target: "feedback",
        title: { th: "ปุ่ม Feedback" },
        body: {
          th: "เจอปัญหาหรืออยากให้ปรับอะไร กด Feedback แล้วคลิกตรงจุดบนหน้าจอที่ต้องการ เขียนสิ่งที่พบ เลือกว่าเป็นปัญหาหรือขอเปลี่ยนแปลง แล้วกดส่ง ระบบจะแนบภาพหน้าจอและรายละเอียดที่ทีมต้องใช้ตรวจสอบให้เอง",
        },
        phoneTarget: "user-menu",
        phoneBody: {
          th: "เจอปัญหาหรืออยากให้ปรับอะไร แตะรูปของคุณที่มุมขวาบน แล้วเลือก Feedback จากนั้นแตะตรงจุดบนหน้าจอที่ต้องการ เขียนสิ่งที่พบ เลือกว่าเป็นปัญหาหรือขอเปลี่ยนแปลง แล้วกดส่ง",
        },
      },
    ],
  },
];
